import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { mainSiteUrl } from "@/mocks/categories";
import { brandArticles } from "@/mocks/brandArticles";

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get("website_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setSubmitStatus("success");
      setEmail("");
      return;
    }
    try {
      const res = await fetch("https://readdy.ai/api/form/d96giukut53jbls7knpg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ email: formData.get("email") as string }).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (res.ok && (parsed as { code?: string }).code === "OK") {
        setSubmitStatus("success");
        setEmail("");
        setFormError("");
      } else {
        const serverMsg = (parsed as { meta?: { message?: string; detail?: string }; message?: string })?.meta?.message
          || (parsed as { message?: string })?.message
          || (parsed as { meta?: { detail?: string } })?.meta?.detail
          || "";
        if (serverMsg && (serverMsg.toLowerCase().includes("spam") || serverMsg.toLowerCase().includes("form data is spam"))) {
          setSubmitStatus("error");
          setFormError(serverMsg);
        } else {
          setSubmitStatus("error");
          setFormError(serverMsg || "Wystąpił błąd. Spróbuj ponownie.");
        }
      }
    } catch {
      setSubmitStatus("error");
      setFormError("Wystąpił błąd połączenia. Spróbuj ponownie.");
    }
  };

  return (
    <footer className="bg-foreground-950 text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-12">
          <div className="md:col-span-1">
            <h3 className="text-2xl md:text-3xl font-heading font-light text-white mb-3">
              {t("footer.subscribe")}
            </h3>
            <p className="text-white/50 text-sm mb-5 leading-relaxed">
              {t("footer.subscribeText")}
            </p>
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="flex flex-col gap-3"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <input
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("footer.subscribePlaceholder")}
                className="bg-transparent border-b border-white/20 px-1 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-white/60 transition-colors"
              />
              <button
                type="submit"
                className="self-start px-6 py-2.5 bg-white text-foreground-950 rounded-full text-sm font-semibold hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                {t("footer.subscribeButton")}
                <i className="ri-arrow-right-line"></i>
              </button>
              {submitStatus === "success" && (
                <p className="text-accent-400 text-xs">Dziękujemy za zapisanie się!</p>
              )}
              {submitStatus === "error" && (
                <p className="text-red-400 text-xs">{formError || "Wystąpił błąd."}</p>
              )}
            </form>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-white/80 mb-4 uppercase tracking-wider">
              {t("footer.navigation")}
            </h4>
            <div className="flex flex-col gap-3">
              <Link
                to="/"
                className="text-white/50 hover:text-white transition-colors text-sm underline cursor-pointer"
              >
                {t("nav.home")}
              </Link>
              <Link
                to="/kakobuy-sheet-przewodnik"
                className="text-white/50 hover:text-white transition-colors text-sm underline cursor-pointer"
              >
                Kakobuy Spreadsheet Przewodnik
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="text-white/50 hover:text-white transition-colors text-sm underline cursor-pointer"
              >
                Kakobuy Sheet
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="text-white/50 hover:text-white transition-colors text-sm underline cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-white/80 mb-4 uppercase tracking-wider">
              Przewodniki Arkuszy
            </h4>
            <div className="flex flex-col gap-2">
              {brandArticles.map((ba) => (
                <Link
                  key={ba.slug}
                  to={`/${ba.slug}`}
                  className="text-white/50 hover:text-white transition-colors text-sm underline cursor-pointer"
                >
                  {ba.brandName} Spreadsheet
                </Link>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-white/80 mb-4 uppercase tracking-wider">
              {t("footer.contact")}
            </h4>
            <div className="space-y-3 text-white/50 text-sm">
              <p>kontakt@kakobuyfinds.wiki</p>
            </div>
            <h4 className="text-sm font-semibold text-white/80 mt-8 mb-3 uppercase tracking-wider">
              {t("footer.address")}
            </h4>
            <div className="space-y-1.5 text-white/50 text-sm">
              <p>Warszawa, Polska</p>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            <a href="https://twitter.com" target="_blank" rel="noopener nofollow" className="text-white/40 hover:text-white transition-colors text-sm underline cursor-pointer">
              Twitter
            </a>
            <a href="https://facebook.com" target="_blank" rel="noopener nofollow" className="text-white/40 hover:text-white transition-colors text-sm underline cursor-pointer">
              Facebook
            </a>
            <a href="https://instagram.com" target="_blank" rel="noopener nofollow" className="text-white/40 hover:text-white transition-colors text-sm underline cursor-pointer">
              Instagram
            </a>
          </div>
          <p className="text-white/30 text-sm text-center">
            {t("footer.copyright")}
          </p>
          <div className="flex items-center gap-5">
            <a href="#" className="text-white/30 hover:text-white/60 transition-colors text-sm underline cursor-pointer">
              {t("footer.privacy")}
            </a>
            <a href="#" className="text-white/30 hover:text-white/60 transition-colors text-sm underline cursor-pointer">
              {t("footer.terms")}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}